import React, { useState, useContext, useMemo, FunctionComponent, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Loadable from 'react-loadable';
import { isUpcomingEnabled } from '@client/helpers';
import { ExpiredOverlay, CartStatusEnum } from '@pegasus/cart/components/expired-overlay';
import { LanguageCode } from '@pegasus/translation/types';
import { useTranslationContext } from '@pegasus/translation/hooks';
import { Header, GuestHeader } from '@pegasus/ui-headers';
import { getHasAccessibleOrange } from '@client/redux/modules/abtesting/accessibleOrange/selectors';
import {
    CookieConsent,
    setCookie,
    isAccepted,
} from '@pegasus/ui-headers/components/cookie-consent';
import TranslationsScope from '@client/components/shared/translation/TranslationsScope';
import { ConfigContext } from '@client/hoc/config/ConfigContext';
import { getToken } from '@client/redux/modules/user/selectors';
import { useCart } from '@client/lib/cart/hooks';
import { getWebviewOs } from '@client/redux/modules/clientDevice/selectors';
import { getExternalCartStatus } from '@client/helpers/pushNotification/utils';
import { COM_DOMAIN } from '@client/config/constants';
import { getSupportedLanguages } from './helpers';
import { useCampaigns } from './hooks';
import { Timer } from './timer';
import FlashMessages from './flash-messages';
import CountrySelection from './CountrySelection/CountrySelection';

type Props = {
    isGuest?: boolean;
};

const LoadableDatePicker = Loadable({
    loader: () => import(/* webpackChunkName: "date-picker" */ './date-picker'),
    loading: () => '',
});

export const HeaderWrapper: FunctionComponent<Props> = ({ isGuest = false }) => {
    const isWebview = !!useSelector(getWebviewOs);
    if (isWebview) {
        return null;
    }

    const { config } = useContext(ConfigContext);
    const { translations } = useTranslationContext();
    const [timerState, setTimerState] = useState<CartStatusEnum>(null);

    const [cart, cartActions] = useCart();
    const cartInfo = cart && { savings: cart.savings, total: cart.total };
    const cartItems = (cart && cart.items) || [];
    const csrfToken = useSelector(getToken);
    const externalCartStatus = getExternalCartStatus() as CartStatusEnum | null;
    const campaigns = useCampaigns();
    const accessibleOrange: boolean = useSelector(getHasAccessibleOrange);

    const supportedLanguages = useCallback(() => getSupportedLanguages(config), [config]);

    const isComDomain = config.appdomain === COM_DOMAIN;
    const isCookieConsentAccepted = isAccepted();

    const extendCart = useCallback(() => {
        cartActions.onExtendCart();
        setTimerState(null);
    }, [cartActions]);

    const renderTimer = useMemo(() => () => (
        <TranslationsScope path="pushNotification">
            <Timer setTimerState={setTimerState} onExpiredCart={cartActions.onExpiredCart} cart={cart} />
        </TranslationsScope>
    ), [cart]);

    useEffect(() => {
        if (externalCartStatus) {
            setTimerState(externalCartStatus);
        }
    }, [externalCartStatus]);

    return (
        <div style={{ boxSizing: 'content-box' }}>
            {isGuest ? (
                <GuestHeader
                    translations={translations}
                    languageCode={translations.locale as LanguageCode}
                    supportedLanguages={supportedLanguages()}
                    isPrive={config.isPriveDomain}
                />
            ) : (
                <Header
                    translations={translations}
                    languageCode={translations.locale as LanguageCode}
                    csrfToken={csrfToken}
                    timer={renderTimer}
                    cart={{
                        items: cartItems,
                        info: cartInfo,
                    }}
                    cartEvents={{
                        onItemRemove: cartActions.onItemRemove,
                    }}
                    abtests={{
                        accessibleOrange
                    }}
                    datePicker={isUpcomingEnabled(config) ? <LoadableDatePicker /> : null}
                    campaigns={campaigns}
                    supportedLanguages={supportedLanguages()}
                    isPrive={config.isPriveDomain}
                />
            )}
            <CookieConsent
                translations={translations}
                accepted={isAccepted()}
                onClose={setCookie}
            />
            {isCookieConsentAccepted && isComDomain &&
                <CountrySelection />
            }
            <ExpiredOverlay
                translations={translations}
                isShown={!!timerState}
                status={timerState}
                closeDialog={() => {
                    setTimerState(null);
                }}
                extendCart={extendCart}
            />
            <FlashMessages />
        </div>
    );
};

export default HeaderWrapper;
